import { z } from 'zod';
import { hqHalfyearPeriodSchema, hqHalfyearTopicSchema } from './hq-halfyear-review';
import { hqHistoricalSummarySchema } from './hq-historical-reports';

const id = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u);
const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/u).refine(value => {
  const parsed = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
});
const count = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
export const hqDashboardQuerySchema = hqHalfyearPeriodSchema.extend({ day: date, includeInspection: z.boolean().optional(), inspectionBasis: z.literal('halfyear').optional() }).strict();
export type HqDashboardQuery = z.infer<typeof hqDashboardQuerySchema>;

export const hqDashboardInspectionSchema = z.object({
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/u), generatedAt: z.string().datetime(), refreshPending: z.boolean(),
  reports: count, findings: count, completed: count, unresolved: count, carriedUnresolved: count,
}).strict().refine(row => row.completed + row.unresolved === row.findings);

const halfyearCounts = hqHalfyearPeriodSchema.extend({
  reports: count, findings: count, completed: count, unresolved: count, carriedUnresolved: count,
}).strict();
export const hqHalfyearInspectionCountsSchema = halfyearCounts.refine(row => row.completed + row.unresolved === row.findings);
export const hqHalfyearInspectionSnapshotSchema = z.object({
  schemaVersion: z.literal(1), coverage: z.literal('complete-source'), through: date,
  periods: z.array(hqHalfyearInspectionCountsSchema).max(200),
}).strict().refine(row => new Set(row.periods.map(period => `${period.year}:${period.half}`)).size === row.periods.length);
export const hqDashboardHalfyearInspectionSchema = halfyearCounts.extend({
  generatedAt: z.string().datetime(), refreshPending: z.boolean(),
}).strict().refine(row => row.completed + row.unresolved === row.findings);

const accident = z.object({
  year: z.number().int().min(2000).max(2200), industrial: count, minor: count,
  property: count, total: count, unclassified: count,
}).strict().refine(row => row.industrial + row.minor + row.property + row.unclassified === row.total);
export const hqDashboardSiteSummarySchema = z.object({
  scope: z.object({ kind: z.literal('site'), tenantId: z.literal('kop'), siteId: id }).strict(),
  day: date, month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/u),
  generatedAt: z.string().datetime(), aggregation: z.literal('daily'), refreshPending: z.boolean(),
  accident, risk: z.null(), education: z.null(), drill: z.null(), work: z.null(),
  tbm: z.null(), inspection: z.null(),
}).strict();
export const hqDashboardReviewSchema = z.object({
  ...hqHalfyearPeriodSchema.shape,
  version: z.number().int().positive(), savedAt: z.string().datetime(),
  topics: z.array(hqHalfyearTopicSchema.pick({ id: true, label: true, status: true })).length(10),
}).strict().refine(row => new Set(row.topics.map(topic => topic.id)).size === 10);
export const hqDashboardResultSchema = z.object({
  ...hqDashboardQuerySchema.shape,
  review: hqDashboardReviewSchema.nullable(),
  reviewState: z.enum(['saved', 'missing', 'invalid']),
  historical: hqHistoricalSummarySchema.nullable().default(null),
  historicalState: z.enum(['saved', 'missing', 'invalid']).default('missing'),
  sites: z.array(z.object({
    siteId: id, displayName: z.string().min(1).max(200),
    kind: z.enum(['factory', 'marine', 'construction-site']),
    reportingDivisionId: id.nullable(),
    summary: hqDashboardSiteSummarySchema.nullable(),
    summaryState: z.enum(['saved', 'missing', 'invalid', 'period-unavailable', 'denied']),
    inspection: hqDashboardInspectionSchema.nullable().default(null),
    inspectionState: z.enum(['saved', 'missing', 'invalid', 'period-unavailable', 'denied']).default('missing'),
    halfyearInspection: hqDashboardHalfyearInspectionSchema.nullable().default(null),
    halfyearInspectionState: z.enum(['saved', 'missing', 'invalid', 'period-unavailable', 'denied']).default('missing'),
  }).strict()).max(200),
}).strict().superRefine((value, context) => {
  if ((value.reviewState === 'saved') !== (value.review !== null) ||
      (value.review && (value.review.year !== value.year || value.review.half !== value.half)))
    context.addIssue({ code: 'custom', message: 'Saved review period or state mismatch' });
  if (new Set(value.sites.map(site => site.siteId)).size !== value.sites.length)
    context.addIssue({ code: 'custom', message: 'Duplicate configured site' });
  for (const site of value.sites) {
    const summary = site.summary;
    if ((site.summaryState === 'saved') !== (summary !== null) || (summary && (
      summary.scope.siteId !== site.siteId || summary.accident.year !== value.year ||
      summary.month !== summary.day.slice(0, 7) || summary.day > value.day ||
      summary.day.slice(0, 4) !== String(value.year))))
      context.addIssue({ code: 'custom', message: 'Saved site projection binding mismatch' });
    if ((site.inspectionState === 'saved') !== (site.inspection !== null) ||
        (site.inspection && site.inspection.month !== value.day.slice(0, 7)))
      context.addIssue({ code: 'custom', message: 'Saved inspection period or state mismatch' });
    if ((site.halfyearInspectionState === 'saved') !== (site.halfyearInspection !== null) ||
        (site.halfyearInspection && (site.halfyearInspection.year !== value.year || site.halfyearInspection.half !== value.half)))
      context.addIssue({ code: 'custom', message: 'Saved halfyear inspection period or state mismatch' });
  }
});
export type HqDashboardResult = z.infer<typeof hqDashboardResultSchema>;
